/**
 * BOARD OF INQUIRY — the three-strike disciplinary system, and what the board
 * actually says.
 *
 * Both halves of the *rule* are documented. The trigger, verbatim: *"If you
 * screw up and shoot down a friendly aircraft during your mission, you'll come
 * before the Board of Inquiry"* (manual, "Board of Inquiry"). The escalation,
 * verbatim: *"The first time you come before the board, you'll be given a mild
 * reprimand. If you commit the same offense a second time, you'll be reprimanded
 * strongly. And if you commit the same offense three times, the board will strip
 * you of your wings and you'll be grounded permanently!"* (manual, "Fly Next
 * Mission").
 *
 * The *wording* is `BOI.DAT`'s, and until 2026-08-26 it was ours. This module
 * used to carry three sentences of its own — *"The board issues a mild
 * reprimand. See that it does not happen again."* — which paraphrased the
 * manual's description of the findings rather than quoting the findings. The
 * file holds the real four texts: one accusation and three graded responses,
 * transcribed below and asserted line-for-line against
 * `packages/formats`' `decodeBoiDat` reading the real `VOLUME.006` bytes by
 * `tools/bake/test/ceremony-text.test.ts`. `%s` is the service.
 *
 * `BOARD_OFFENCES` is the enumerable register of what this port can put a pilot
 * in front of the board for. It has exactly one member, because friendly fire is
 * the only offence either source names — docs/game/campaign.md keeps "the full
 * set of Board-of-Inquiry-triggering offenses" as an open question, and a
 * register that says so is better than a guess dressed up as a rule.
 *
 * One hearing per sortie, not one per aeroplane: the board convenes over a
 * mission, and a pilot who shoots down two of his own in one fight has made one
 * appalling mistake rather than two.
 */

export const BOARD_OFFENCES = ["friendlyFire"] as const;

export type BoardOffence = (typeof BOARD_OFFENCES)[number];

export const BOARD_OFFENCE_LABELS: Readonly<Record<BoardOffence, string>> = {
  friendlyFire: "Shooting down an aircraft of your own side",
};

/** Verbatim `PS.EXE`, and the screen's title. */
export const BOARD_HEADER = "BOARD OF INQUIRY";

/** `BOI.DAT` section 0 — the line the board opens with. */
export const BOARD_ACCUSATION = "You attacked and shot down our aircraft!";

/** Three strikes ends a career, per the manual — and `BOI.DAT` holds exactly three findings. */
export const STRIKES_BEFORE_DISCHARGE = 3;

/** `BOI.DAT` sections 1–3, mildest first, `%s` unexpanded. */
export const BOARD_FINDINGS: readonly (readonly string[])[] = [
  [
    "The %s is not pleased with this incident.",
    "However, since it is your first offense of",
    "this type, they have decided to overlook it.",
  ],
  [
    "The %s is not pleased with this incident.",
    "They warn that if it happens again, you'll",
    "be back in the trenches.",
  ],
  [
    "The %s is not pleased with your flagrant disregard",
    "for military discipline. You are stripped of your wings",
    "and must spend the remainder of the war in the trenches.",
  ],
];

export interface BoardHearing {
  readonly offence: BoardOffence;
  /** How many times this pilot has now been before the board. */
  readonly strike: number;
  /** The board's opening line, verbatim. */
  readonly accusation: string;
  /** The finding, as the file breaks it, `%s` filled. */
  readonly lines: readonly string[];
  /** The same finding as one sentence, for the career log. */
  readonly finding: string;
  /** True on the third: wings stripped, grounded permanently. */
  readonly grounded: boolean;
}

export interface BoardInput {
  /** Friendly aeroplanes the player shot down this sortie. */
  readonly friendlyKills: number;
  /** Strikes already on the record. */
  readonly strikes: number;
  /** What `%s` fills with — `Royal Flying Corps`, `Royal Air Force`, `German Air Service`. */
  readonly service: string;
}

/**
 * The hearing this sortie earns, or nothing if the pilot kept his aim honest.
 *
 * A fourth offence cannot happen — the third grounds the career and
 * `applyDebrief` refuses a debriefing from a discharged pilot — but the clamp is
 * here anyway, because the alternative to clamping is an undefined finding on a
 * screen.
 */
export function boardHearing(input: BoardInput): BoardHearing | undefined {
  if (input.friendlyKills <= 0) return undefined;
  const strike = input.strikes + 1;
  const stored = BOARD_FINDINGS[Math.min(strike, BOARD_FINDINGS.length) - 1] ?? [];
  const lines = stored.map((line) => line.replaceAll("%s", input.service));
  return {
    offence: "friendlyFire",
    strike,
    accusation: BOARD_ACCUSATION,
    lines,
    finding: lines.join(" "),
    grounded: strike >= STRIKES_BEFORE_DISCHARGE,
  };
}
